// Record sync support for the grid controller.
//
// Handles record-created events for the grid:
//   - Add-new: assigns the new value to a pending row (registered by the
//     cell editor before it's destroyed) and triggers autosave.
//   - Edit: refreshes labels on all rows referencing the edited record.
//
// The cell editor dispatches a grid:pendingAssignment event before AG Grid
// destroys it, capturing the row/field context. This module consumes that
// pending state when the matching record-created fires.
//
// Usage from the grid controller:
//   import * as recordSync from "../grid/record_sync"
//
//   connect()    → recordSync.install(this)
//   disconnect() → recordSync.uninstall(this)

export function install(controller) {
  controller.pendingAssignment = null

  controller._recordSync_onPending = (event) => {
    controller.pendingAssignment = event.detail
  }
  controller.element.addEventListener("grid:pendingAssignment", controller._recordSync_onPending)

  controller._recordSync_onCreated = (event) => {
    onRecordCreated(controller, event)
  }
  document.addEventListener("record-created", controller._recordSync_onCreated)
}

export function uninstall(controller) {
  controller.element.removeEventListener("grid:pendingAssignment", controller._recordSync_onPending)
  document.removeEventListener("record-created", controller._recordSync_onCreated)
  controller.pendingAssignment = null
}

function onRecordCreated(controller, event) {
  const { gridApi, columnDefsValue, behaviorsValue } = controller
  if (!gridApi) return

  const { storageKey, value, label } = event.detail
  if (!storageKey) return

  // Add-new flow: if a cell editor registered a pending assignment for this
  // storageKey, assign the new value to the target row and trigger autosave.
  const pending = controller.pendingAssignment
  if (pending && pending.storageKey === storageKey) {
    controller.pendingAssignment = null

    const rowNode = gridApi.getRowNode(String(pending.rowId))
    if (rowNode) {
      if (pending.field) rowNode.data[pending.field] = label
      if (pending.valueField) rowNode.data[pending.valueField] = value

      gridApi.applyTransaction({ update: [rowNode.data] })
      gridApi.refreshCells({ rowNodes: [rowNode], force: true })

      controller.dispatch("cellChanged", {
        detail: { field: pending.field, oldValue: null, newValue: label, data: rowNode.data }
      })
    }
  }

  // Edit flow: refresh labels on all rows where the valueField already
  // matches. This covers edits (record renamed) and is harmless for
  // add-new (the just-assigned row gets the same label again).
  for (const colDef of columnDefsValue) {
    if (!colDef.behavior) continue
    const config = behaviorsValue[colDef.behavior]
    if (!config || config.storageKey !== storageKey) continue

    const field = colDef.field
    const valueField = config.valueField
    if (!valueField) continue

    const updatedNodes = []
    gridApi.forEachNode((node) => {
      if (!node.data) return
      if (String(node.data[valueField] || "") === String(value)) {
        if (field) node.data[field] = label
        updatedNodes.push(node)
      }
    })
    if (updatedNodes.length > 0) {
      gridApi.applyTransaction({ update: updatedNodes.map((n) => n.data) })
      gridApi.refreshCells({ rowNodes: updatedNodes, force: true })
    }
  }
}
